import React, { useState, useRef, useEffect } from 'react';
import ImageUploading, { ImageListType } from 'react-images-uploading';
import { Button, Form, Modal, Header } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { FlexColumn, FlexRow } from '../layouts';
import { useDebounceEffect, canvasPreview, toImageURL } from '../utility';
import { trackPromise } from 'react-promise-tracker';

export interface IUploadImageModal {
  open: boolean;
  title: string;
  currentImage: string | undefined;

  onCancel(): void;
  onSubmit(_imageURL: string): void;
}

const UploadImageModal = (props: IUploadImageModal) => {
  const { open, title, currentImage, onCancel, onSubmit } = props;

  const [image, setImage] = useState<ImageListType>([]);
  const [crop, setCrop] = useState<Crop>({
    unit: 'px', // Can be 'px' or '%'
    x: 25,
    y: 25,
    width: 100,
    height: 100,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale] = useState(1);
  const [rotate] = useState(0);

  const { t } = useTranslation();
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (currentImage) {
      setImage([{ dataURL: currentImage }]);
    }
  }, [currentImage, setImage]);

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        await canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          scale,
          rotate
        );
      }
    },
    100,
    [completedCrop, scale, rotate]
  );

  const handleChange = (
    imageList: ImageListType,
    addUpdateIndex: number[] | undefined
  ) => {
    // data for submit
    console.log(imageList, addUpdateIndex);
    setImage(imageList);
  };

  const handleCancel = () => {
    setImage([]);
    onCancel();
  };

  const handleSubmit = () => {
    if (completedCrop?.width && completedCrop?.height && imgRef.current) {
      trackPromise(toImageURL(imgRef.current, completedCrop, scale)).then(
        (data) => {
          onSubmit(data);
        }
      );
    }
  };

  return (
    <Modal open={open} as={Form}>
      <Modal.Header>{title}</Modal.Header>
      <Modal.Content>
        <ImageUploading value={image} onChange={handleChange}>
          {({ imageList, onImageUpload, onImageUpdate, onImageRemove }) => (
            <div className="upload__image-wrapper">
              <FlexRow padBottom={20}>
                <Button onClick={onImageUpload} color="green">
                  {t('uploadImage.selectImage')}
                </Button>
              </FlexRow>
              {image.length === 0 && <div style={{ height: 300 }} />}
              {imageList.map((image, index) => (
                <FlexRow key={index}>
                  <ReactCrop
                    crop={crop}
                    onChange={(_, percentCrop) => setCrop(percentCrop)}
                    onComplete={(c) => setCompletedCrop(c)}
                    aspect={1}
                  >
                    <img
                      src={image.dataURL}
                      width="400"
                      ref={imgRef}
                      alt="Crop me"
                    />
                  </ReactCrop>
                  <FlexRow padLeft={20}>
                    {completedCrop && Boolean(completedCrop) && (
                      <FlexColumn>
                        <Header as="h4">{t('uploadImage.preview')}</Header>
                        <div>
                          <canvas
                            ref={previewCanvasRef}
                            style={{
                              border: '1px solid black',
                              objectFit: 'contain',
                              width: completedCrop.width,
                              height: completedCrop.height,
                            }}
                          />
                        </div>
                      </FlexColumn>
                    )}
                  </FlexRow>
                </FlexRow>
              ))}
              <FlexRow padTop={20}>
                <Button onClick={() => onImageUpdate(0)}>Update</Button>
                <Button onClick={() => onImageRemove(0)}>Remove</Button>
              </FlexRow>
            </div>
          )}
        </ImageUploading>
        <Modal.Actions>
          <FlexRow fullWidth right>
            <Button onClick={handleCancel} color="grey">
              {t('general.cancel')}
            </Button>
            <Button
              onClick={() => handleSubmit()}
              color="green"
              disabled={image.length === 0}
            >
              {t('general.save')}
            </Button>
          </FlexRow>
        </Modal.Actions>
      </Modal.Content>
    </Modal>
  );
};

export default UploadImageModal;
